/**
 * Product Tour — /business/product-tour
 *
 * Combines:
 *   - Core product features (B2BPlatformCenter grid)
 *   - Integrations info
 *   - Security info
 *   - System Status info
 *
 * No simulations, no illustration placeholders, no extra CTAs.
 */

import type { LucideIcon } from 'lucide-react';
import { motion } from 'framer-motion';
import {
  Activity,
  ArrowRight,
  BarChart3,
  Bell,
  Building2,
  Calendar,
  Code,
  FileText,
  Globe,
  Key,
  Link2,
  Lock,
  Mail,
  Shield,
  ShieldCheck,
  Sparkles,
  Users,
  Zap,
} from 'lucide-react';
import { B2BPlatformCenter } from '@modules/public/landing/B2BPlatformCenter';
import { B2BMarketingPageLayout } from './_shared/B2BMarketingPageLayout';

// ── fade-up helper ────────────────────────────────────────────────────────────
function FadeUp({
  children,
  delay = 0,
  className = '',
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.45, delay }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

// ── shared sub-section types ──────────────────────────────────────────────────
type InfoCard = { icon: LucideIcon; iconColor: string; title: string; body: string };
type StatItem = { value: string; label: string; sub?: string };

// ── info section component ────────────────────────────────────────────────────
function InfoSection({
  eyebrow,
  title,
  description,
  stats,
  cards,
  bg = '',
}: {
  eyebrow: string;
  title: string;
  description: string;
  stats: StatItem[];
  cards: InfoCard[];
  bg?: string;
}) {
  return (
    <section className={`px-4 py-14 sm:px-6 md:px-8 md:py-18 ${bg}`}>
      <FadeUp className="mx-auto max-w-5xl">
        {/* heading */}
        <div className="mb-8 border-b border-gray-100 pb-6 dark:border-zinc-800">
          <p className="text-[11px] font-semibold uppercase tracking-[0.13em] text-teal-600 dark:text-teal-400">
            {eyebrow}
          </p>
          <h2 className="mt-1.5 text-xl font-bold text-gray-900 dark:text-white sm:text-2xl">
            {title}
          </h2>
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-gray-500 dark:text-zinc-400">
            {description}
          </p>
        </div>

        {/* stats row */}
        <div className="mb-8 grid grid-cols-2 gap-4 sm:grid-cols-4">
          {stats.map((s) => (
            <div
              key={s.label}
              className="rounded-xl border border-gray-100 bg-white px-4 py-3 dark:border-zinc-700/40 dark:bg-zinc-800/50"
            >
              <p className="text-lg font-bold text-teal-600 dark:text-teal-400">{s.value}</p>
              <p className="mt-0.5 text-xs font-medium text-gray-700 dark:text-zinc-200">{s.label}</p>
              {s.sub && <p className="mt-0.5 text-[10px] text-gray-400 dark:text-zinc-500">{s.sub}</p>}
            </div>
          ))}
        </div>

        {/* cards grid */}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {cards.map(({ icon: Icon, iconColor, title: cardTitle, body }, i) => (
            <FadeUp key={cardTitle} delay={0.04 * i}>
              <div className="flex gap-3 rounded-xl border border-gray-100 bg-white p-4 dark:border-zinc-700/40 dark:bg-zinc-800/50">
                <div className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${iconColor}`}>
                  <Icon className="h-4 w-4" aria-hidden />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-800 dark:text-zinc-100">{cardTitle}</p>
                  <p className="mt-0.5 text-xs leading-relaxed text-gray-500 dark:text-zinc-400">{body}</p>
                </div>
              </div>
            </FadeUp>
          ))}
        </div>
      </FadeUp>
    </section>
  );
}

// ── data ──────────────────────────────────────────────────────────────────────
const HERO_STATS = [
  { value: '3×', label: 'Faster time-to-shortlist' },
  { value: '94%', label: 'AI match accuracy' },
  { value: '60%', label: 'Less admin overhead' },
  { value: '40K+', label: 'Candidates processed daily' },
];

const INTEGRATIONS_STATS: StatItem[] = [
  { value: '40+', label: 'Pre-built integrations', sub: 'and growing' },
  { value: '99.9%', label: 'Sync uptime SLA', sub: 'enterprise tier' },
  { value: '<100ms', label: 'API response time', sub: 'P99 latency' },
  { value: 'REST+GQL', label: 'API standards', sub: 'versioned endpoints' },
];

const INTEGRATIONS_CARDS: InfoCard[] = [
  { icon: Mail,      iconColor: 'bg-violet-100 text-violet-700', title: 'Email & calendar',    body: 'Two-way sync with Gmail, Outlook, and Google Calendar.' },
  { icon: Building2, iconColor: 'bg-indigo-100 text-indigo-700', title: 'HRIS connectors',     body: 'Push hire data to Workday, BambooHR, Keka, and more.' },
  { icon: Code,      iconColor: 'bg-sky-100 text-sky-700',       title: 'REST & GraphQL APIs', body: 'Build custom workflows with scoped keys and signed webhooks.' },
  { icon: Zap,       iconColor: 'bg-emerald-100 text-emerald-700', title: 'Automation triggers', body: 'Zapier and native workflow rules for stage-change events.' },
  { icon: Link2,     iconColor: 'bg-amber-100 text-amber-700',   title: 'Job board posting',  body: 'Publish to LinkedIn, Indeed, and Naukri in one click.' },
  { icon: Globe,     iconColor: 'bg-teal-100 text-teal-700',     title: 'Marketplace',        body: 'Browse upcoming integrations and submit requests in-app.' },
];

const SECURITY_STATS: StatItem[] = [
  { value: 'AES-256', label: 'Encryption at rest',   sub: 'managed key service' },
  { value: 'TLS 1.3', label: 'In-transit encryption', sub: 'all endpoints' },
  { value: 'RBAC',    label: 'Role-based access',     sub: '5-tier permission model' },
  { value: '100%',    label: 'Action audit coverage', sub: 'tamper-resistant logs' },
];

const SECURITY_CARDS: InfoCard[] = [
  { icon: ShieldCheck, iconColor: 'bg-violet-100 text-violet-700', title: 'SOC 2 aligned',       body: 'Controls, policies, and evidence aligned to SOC 2 Type II criteria.' },
  { icon: Lock,        iconColor: 'bg-indigo-100 text-indigo-700', title: 'Encryption everywhere', body: 'AES-256 at rest, TLS 1.3 in transit, column-level masking where needed.' },
  { icon: Key,         iconColor: 'bg-sky-100 text-sky-700',       title: 'SSO & MFA ready',     body: 'SAML 2.0 and OIDC for enterprise IdPs; enforced MFA per org policy.' },
  { icon: FileText,    iconColor: 'bg-emerald-100 text-emerald-700', title: 'Audit logs',         body: 'Every admin and recruiter action timestamped and attributable.' },
  { icon: Shield,      iconColor: 'bg-amber-100 text-amber-700',   title: 'GDPR tooling',        body: 'DPA available; data subject request workflows built into the product.' },
  { icon: Globe,       iconColor: 'bg-teal-100 text-teal-700',     title: 'Global data residency', body: 'Choose data region at onboarding; subprocessors listed in Trust Center.' },
];

const STATUS_STATS: StatItem[] = [
  { value: '99.97%',    label: 'Rolling 90d uptime',    sub: 'core API' },
  { value: '<1 min',    label: 'Incident detection',    sub: 'automated monitoring' },
  { value: '24/7',      label: 'On-call coverage',      sub: 'P1 incidents' },
  { value: 'Global CDN', label: 'Edge delivery',        sub: 'sub-50ms TTFB' },
];

const STATUS_CARDS: InfoCard[] = [
  { icon: Activity,  iconColor: 'bg-emerald-100 text-emerald-700', title: 'Real-time health',      body: 'Component-level status refreshed every 60 seconds.' },
  { icon: Bell,      iconColor: 'bg-violet-100 text-violet-700',   title: 'Incident alerts',       body: 'Subscribe by email or webhook for instant notification.' },
  { icon: FileText,  iconColor: 'bg-sky-100 text-sky-700',         title: 'Post-mortems',          body: 'Public incident summaries with root cause and remediation.' },
  { icon: Calendar,  iconColor: 'bg-amber-100 text-amber-700',     title: 'Maintenance windows',   body: 'Planned downtime announced 72 hours in advance.' },
  { icon: BarChart3, iconColor: 'bg-indigo-100 text-indigo-700',   title: 'Historical uptime',     body: '90-day component uptime chart — downloadable as PDF.' },
  { icon: Globe,     iconColor: 'bg-teal-100 text-teal-700',       title: 'Regional health',       body: 'Separate status feeds for US, EU, and APAC regions.' },
];

// ── page ─────────────────────────────────────────────────────────────────────
export function ProductTourPage() {
  return (
    <B2BMarketingPageLayout>

      {/* ══════════════════════════════════════════════════
          HERO — very light teal gradient band
      ══════════════════════════════════════════════════ */}
      <section
        className="relative overflow-hidden px-4 pb-14 pt-14 sm:px-6 md:px-8 md:pb-16 md:pt-20"
        style={{
          background:
            'linear-gradient(150deg, #F0FDFA 0%, #CCFBF1 35%, #F0FDFA 65%, #E0FBF6 100%)',
        }}
      >
        <div
          className="pointer-events-none absolute -left-20 -top-20 h-72 w-72 rounded-full opacity-50 blur-3xl"
          style={{ background: 'rgba(20,184,166,0.15)' }}
          aria-hidden
        />
        <div
          className="pointer-events-none absolute -right-16 bottom-0 h-56 w-56 rounded-full opacity-40 blur-3xl"
          style={{ background: 'rgba(52,211,153,0.18)' }}
          aria-hidden
        />

        <FadeUp className="relative z-10 mx-auto max-w-2xl text-center">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-teal-200 bg-teal-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.13em] text-teal-700">
            <Sparkles className="h-3 w-3" aria-hidden />
            Product Tour
          </span>
          <h1 className="mt-4 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl lg:text-5xl">
            See GoGrabJob
            <span className="block text-teal-600"> in Action</span>
          </h1>
          <p className="mt-4 text-base leading-relaxed text-gray-600 sm:text-lg">
            Every capability your recruitment team needs — AI matching, pipeline management,
            structured interviews, integrations, and enterprise security — in one workspace.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <a
              href="/business/demo"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-xl bg-teal-600 px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-teal-200/60 transition-colors hover:bg-teal-700"
            >
              Book a Demo
              <ArrowRight className="h-4 w-4" aria-hidden />
            </a>
            <a
              href="/business/features"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-xl border border-teal-200 bg-white/80 px-5 py-2.5 text-sm font-semibold text-teal-700 transition-colors hover:bg-teal-50"
            >
              All Features
            </a>
          </div>
        </FadeUp>
      </section>

      {/* ══════════════════════════════════════════════════
          STATS STRIP
      ══════════════════════════════════════════════════ */}
      <section className="border-b border-gray-100 bg-white/70 px-4 py-8 dark:border-zinc-800 dark:bg-zinc-900/60 sm:px-6 md:px-8">
        <FadeUp>
          <div className="mx-auto grid max-w-4xl grid-cols-2 gap-6 md:grid-cols-4">
            {HERO_STATS.map((s) => (
              <div key={s.label} className="text-center">
                <p className="text-3xl font-extrabold text-teal-600 dark:text-teal-400">{s.value}</p>
                <p className="mt-1 text-xs text-gray-500 dark:text-zinc-400">{s.label}</p>
              </div>
            ))}
          </div>
        </FadeUp>
      </section>

      {/* ══════════════════════════════════════════════════
          FEATURE GRID  (mirrors FeaturesPage)
      ══════════════════════════════════════════════════ */}
      <B2BPlatformCenter />

      {/* ══════════════════════════════════════════════════
          INTEGRATIONS
      ══════════════════════════════════════════════════ */}
      <InfoSection
        eyebrow="Integrations"
        title="Connect your entire hiring stack"
        description="40+ integrations — email, calendar, HRIS, assessments, and APIs. GoGrabJob syncs with the tools your team already relies on."
        stats={INTEGRATIONS_STATS}
        cards={INTEGRATIONS_CARDS}
        bg="border-t border-gray-100 bg-gray-50/50 dark:border-zinc-800 dark:bg-zinc-900/30"
      />

      {/* ══════════════════════════════════════════════════
          SECURITY
      ══════════════════════════════════════════════════ */}
      <InfoSection
        eyebrow="Security"
        title="Security built for enterprise hiring"
        description="GDPR-aligned, encrypted, role-gated, and fully auditable. Recruiting data is personal — we protect it with defence-in-depth."
        stats={SECURITY_STATS}
        cards={SECURITY_CARDS}
        bg="border-t border-gray-100 bg-white/60 dark:border-zinc-800 dark:bg-zinc-900/20"
      />

      {/* ══════════════════════════════════════════════════
          SYSTEM STATUS
      ══════════════════════════════════════════════════ */}
      <InfoSection
        eyebrow="System Status"
        title="Reliability you can depend on"
        description="Live uptime, incident history, and scheduled maintenance windows — published openly so your team can plan around platform availability."
        stats={STATUS_STATS}
        cards={STATUS_CARDS}
        bg="border-t border-gray-100 bg-gray-50/50 dark:border-zinc-800 dark:bg-zinc-900/30"
      />

      {/* ══════════════════════════════════════════════════
          BOTTOM CTA CARD
      ══════════════════════════════════════════════════ */}
      <section className="border-t border-gray-100 px-4 py-16 dark:border-zinc-800 sm:px-6 md:px-8 md:py-20">
        <FadeUp>
          <div
            className="relative mx-auto max-w-3xl overflow-hidden rounded-3xl p-10 text-center sm:p-14"
            style={{
              background:
                'linear-gradient(135deg, #0F766E 0%, #0D9488 50%, #14B8A6 100%)',
            }}
          >
            <div
              className="pointer-events-none absolute inset-0 rounded-3xl"
              style={{
                background:
                  'radial-gradient(ellipse 70% 60% at 50% 0%, rgba(255,255,255,0.12) 0%, transparent 70%)',
              }}
              aria-hidden
            />
            <span className="relative inline-flex items-center gap-1.5 rounded-full bg-white/20 px-3 py-1 text-[10px] font-semibold uppercase tracking-widest text-white/90 backdrop-blur-sm">
              <Zap className="h-3 w-3" aria-hidden />
              Start Hiring Smarter
            </span>
            <h2 className="relative mt-4 text-2xl font-bold text-white sm:text-3xl">
              Turn Open Roles into Great Hires — Fast
            </h2>
            <p className="relative mt-3 text-sm leading-relaxed text-white/75">
              Kick off your first AI-matched shortlist today and see why 2,000+ recruitment teams
              choose GoGrabJob to move faster and hire better.
            </p>
            <div className="relative mt-8 flex flex-wrap justify-center gap-3">
              <a
                href="/business/demo"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-xl border-2 border-white bg-white px-6 py-2.5 text-sm font-semibold text-teal-800 transition-colors hover:bg-teal-50"
              >
                Book a Demo
                <ArrowRight className="h-4 w-4" aria-hidden />
              </a>
            </div>
            <div className="relative mt-8 flex flex-wrap justify-center gap-5">
              {[
                { Icon: Users,    label: '2,000+ Teams' },
                { Icon: Building2, label: 'Enterprise Ready' },
                { Icon: Lock,     label: 'SOC 2 Certified' },
              ].map(({ Icon, label }) => (
                <div key={label} className="flex items-center gap-1.5 text-xs text-white/70">
                  <Icon className="h-3.5 w-3.5" aria-hidden />
                  {label}
                </div>
              ))}
            </div>
          </div>
        </FadeUp>
      </section>

    </B2BMarketingPageLayout>
  );
}
